import { IEmailPerformanceTotals, IEmailTemplateStats } from 'shared';

/** Whole-number-ish rate as 0-100, or null when there is nothing to divide by */
export function rate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

export function openRate(t: IEmailPerformanceTotals) {
  return rate(t.opened, t.delivered);
}

export function clickRate(t: IEmailPerformanceTotals) {
  return rate(t.clicked, t.delivered);
}

export function spamRate(t: IEmailPerformanceTotals) {
  return rate(t.spam, t.delivered);
}

export function paidWithin7Rate(t: IEmailPerformanceTotals) {
  return rate(t.paidWithin7, t.invoiceEmails);
}

export function formatPct(value: number | null, dp = 1) {
  return value === null ? `–` : `${value.toFixed(dp)}%`;
}

export function formatCount(value: number) {
  return value.toLocaleString(`en-US`);
}

export interface IDelta {
  label: string;
  direction: `up` | `down` | `flat`;
}

/** Percentage-point change between two rates */
export function pointsDelta(current: number | null, previous: number | null): IDelta | null {
  if (current === null || previous === null) {
    return null;
  }

  const diff = current - previous;
  const sign = diff > 0 ? `+` : diff < 0 ? `−` : ``;

  return { label: `${sign}${Math.abs(diff).toFixed(1)} pts`, direction: diff > 0 ? `up` : diff < 0 ? `down` : `flat` };
}

/** Relative change between two counts */
export function percentDelta(current: number, previous: number): IDelta | null {
  if (previous <= 0) {
    return null;
  }

  const diff = ((current - previous) / previous) * 100;
  const sign = diff > 0 ? `+` : diff < 0 ? `−` : ``;

  return { label: `${sign}${Math.abs(diff).toFixed(1)}%`, direction: diff > 0 ? `up` : diff < 0 ? `down` : `flat` };
}

export function templateLabel(t: Pick<IEmailTemplateStats, `templateId` | `templateName`>) {
  if (!t.templateId) {
    return `No template`;
  }

  return t.templateName ?? `Deleted template`;
}

export function toCsv(rows: Array<Array<string | number | null>>) {
  const escape = (v: string | number | null) => {
    // Template names and subjects are user-typed. Spreadsheets run cells that
    // start with a formula trigger, so neutralise those with a leading apostrophe.
    // Numbers are left alone so negative values stay numeric.
    const s = v === null ? `` : typeof v === `string` && /^[=+\-@\t\r]/.test(v) ? `'${v}` : String(v);

    return /[",\n]/.test(s) ? `"${s.replace(/"/g, `""`)}"` : s;
  };

  return rows.map(r => r.map(escape).join(`,`)).join(`\n`);
}
